import React, {useCallback, useEffect, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {useProducts} from '@salesforce/commerce-sdk-react'

const MAX_PRODUCT_IDS_PER_PRODUCTS_REQUEST = 24

const chunkProductIds = (ids, size = MAX_PRODUCT_IDS_PER_PRODUCTS_REQUEST) => {
    const chunks = []

    for (let i = 0; i < ids.length; i += size) {
        const chunkIds = ids.slice(i, i + size)

        chunks.push({
            key: chunkIds.join(','),
            ids: chunkIds
        })
    }

    return chunks
}

const ProductChunk = ({chunk, parameters, onLoad}) => {
    const {data: products, isError} = useProducts(
        {
            parameters: {
                ...parameters,
                ids: chunk.key
            }
        },
        {
            enabled: Boolean(chunk.key)
        }
    )

    useEffect(() => {
        if (products?.data || isError) {
            onLoad(chunk.key, products?.data ?? [])
        }
    }, [chunk.key, products, isError, onLoad])

    return null
}

ProductChunk.propTypes = {
    chunk: PropTypes.shape({
        key: PropTypes.string.isRequired,
        ids: PropTypes.arrayOf(PropTypes.string).isRequired
    }).isRequired,
    parameters: PropTypes.object,
    onLoad: PropTypes.func.isRequired
}

const ProductChunkLoader = ({productIds, parameters = {}, onProductsChange, onLoadingChange}) => {
    const uniqueProductIds = useMemo(
        () => Array.from(new Set((productIds ?? []).filter(Boolean))),
        [productIds]
    )

    const productIdChunks = useMemo(() => chunkProductIds(uniqueProductIds), [uniqueProductIds])
    const productChunkKeys = useMemo(
        () => productIdChunks.map((chunk) => chunk.key).join('|'),
        [productIdChunks]
    )

    const [productsByChunk, setProductsByChunk] = useState({})

    useEffect(() => {
        setProductsByChunk({})
    }, [productChunkKeys])

    const handleChunkLoad = useCallback((chunkKey, productsData) => {
        setProductsByChunk((currentProductsByChunk) => ({
            ...currentProductsByChunk,
            [chunkKey]: productsData
        }))
    }, [])

    const chunksLoaded =
        productIdChunks.length === 0 ||
        productIdChunks.every((chunk) =>
            Object.prototype.hasOwnProperty.call(productsByChunk, chunk.key)
        )

    const productsData = useMemo(
        () => productIdChunks.flatMap((chunk) => productsByChunk[chunk.key] ?? []),
        [productIdChunks, productsByChunk]
    )

    useEffect(() => {
        onLoadingChange?.(!chunksLoaded)
    }, [chunksLoaded, onLoadingChange])

    useEffect(() => {
        if (chunksLoaded) {
            onProductsChange(productsData)
        }
    }, [chunksLoaded, productsData, onProductsChange])

    return (
        <>
            {productIdChunks.map((chunk) => (
                <ProductChunk
                    key={chunk.key}
                    chunk={chunk}
                    parameters={parameters}
                    onLoad={handleChunkLoad}
                />
            ))}
        </>
    )
}

ProductChunkLoader.propTypes = {
    productIds: PropTypes.arrayOf(PropTypes.string),
    parameters: PropTypes.object,
    onProductsChange: PropTypes.func.isRequired,
    onLoadingChange: PropTypes.func
}

export default ProductChunkLoader
